import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api/client';
import { useCart } from './cart';

interface Product {
  productId: string;
  name: string;
  description?: string;
  priceCents: number;
  imageUrl?: string;
  category?: string;
}

const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export function Store() {
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.get<Product[]>('/products') });
  const cart = useCart();
  const navigate = useNavigate();
  const [added, setAdded] = useState<string | null>(null);

  const addToCart = (p: Product) => {
    cart.add({ productId: p.productId, name: p.name, priceCents: p.priceCents, ...(p.imageUrl ? { imageUrl: p.imageUrl } : {}) });
    setAdded(p.productId);
    setTimeout(() => setAdded((cur) => (cur === p.productId ? null : cur)), 1600);
  };

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Store</h1>
          <div className="sub">FBI-certified fingerprint scanners — shipped to your location.</div>
        </div>
        {cart.count > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/app/cart')}>
            View cart ({cart.count})
          </button>
        )}
      </div>

      {products.isLoading && <p className="loading-screen">// loading catalog…</p>}
      {products.data && products.data.length === 0 && (
        <div className="panel empty">
          <h3>No products available yet</h3>
          <p>Your administrator hasn't published any store products.</p>
        </div>
      )}

      <div className="store-grid">
        {products.data?.map((p) => (
          <div className="store-card" key={p.productId}>
            <div className="store-media">
              {p.imageUrl ? <img src={p.imageUrl} alt={p.name} /> : <ScannerGlyph />}
            </div>
            <div className="store-body">
              {p.category && <span className="wf-tag">{p.category}</span>}
              <h3>{p.name}</h3>
              {p.description && <p className="store-desc">{p.description}</p>}
              <div className="store-foot">
                <span className="store-price">{money(p.priceCents)}</span>
                <button
                  className={`btn btn-sm ${added === p.productId ? 'btn-ghost' : 'btn-primary'}`}
                  onClick={() => addToCart(p)}
                >
                  {added === p.productId ? '✓ Added' : 'Add to cart'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScannerGlyph() {
  return (
    <svg width="70" height="70" viewBox="0 0 48 48" fill="none" aria-hidden style={{ opacity: 0.55 }}>
      <rect x="7" y="10" width="34" height="28" rx="4" stroke="var(--accent)" strokeWidth="2" />
      <path d="M17 24c0-4 3-7 7-7s7 3 7 7M20 24c0-2.2 1.8-4 4-4s4 1.8 4 4M23 24c0-.6.4-1 1-1s1 .4 1 1"
        stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M13 31h22" stroke="var(--accent-bright)" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
    </svg>
  );
}
